
// Fichier: /pages/ContactPage.tsx (ou équivalent)
// Version adaptée pour SIS, logique métier conservée.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
// Assurez-vous que les chemins d'import sont corrects
import { PublicLayout } from '../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { sendContactEmail } from '../../services/emailService';
import { useToast } from '../../components/ui/use-toast';
import { Loader2, Mail, Phone, MapPin, Headset, ChevronDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useTenantContext } from '../../context/TenantContext';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

type SendContactEmailResponse = { success: boolean; error?: unknown };

export const ContactPage: React.FC = () => {
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Thème Contact (preview > tenant)
  const contactTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'Wrench',  color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Server',  color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Headset', color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Shield',  color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);

  // Options personnalisables pour le champ "Objet de votre demande"
  type ContactSubjectOption = {
    id: string;
    label: string;
    value: string;
    textColor?: string;
    backgroundColor?: string;
  };

  const subjectOptions: ContactSubjectOption[] = React.useMemo(() => {
    const themeText = (tenant?.config?.color3 as string) || '#ffffff';
    const themeBg = '#111827';
    const defaults: ContactSubjectOption[] = [
      { id: 'info', label: "Demande d'information", value: "Demande d'information", textColor: themeText, backgroundColor: themeBg },
      { id: 'devis', label: 'Demande de devis', value: 'Demande de devis', textColor: themeText, backgroundColor: themeBg },
      { id: 'support', label: 'Support client', value: 'Support client', textColor: themeText, backgroundColor: themeBg },
      { id: 'partenariat', label: 'Proposition de partenariat', value: 'Partenariat', textColor: themeText, backgroundColor: themeBg },
      { id: 'autre', label: 'Autre demande', value: 'Autre demande', textColor: themeText, backgroundColor: themeBg },
    ];

    const custom = (previewConfig as any)?.pages?.contact?.subjectOptions
      ?? (tenant?.config as any)?.pages?.contact?.subjectOptions;
    if (Array.isArray(custom) && custom.length > 0) {
      return custom.map((o: any, idx: number) => ({
        id: o.id || `opt_${idx}`,
        label: o.label ?? o.value ?? '',
        value: o.value ?? o.label ?? '',
        textColor: o.textColor || themeText,
        backgroundColor: o.backgroundColor || themeBg,
      }));
    }
    return defaults;
  }, [tenant?.config?.color3, previewConfig, tenant?.config]);

  const contactHeader = React.useMemo(() => {
    const source = (previewConfig as any)?.pages?.contact?.header
      ?? (tenant?.config as any)?.pages?.contact?.header
      ?? {};
    const defaults = {
      badgeLabel: 'Entrer en contact',
      title: `Contacter ${tenant?.name || 'notre équipe'}`,
      line1: "Une question, une demande d'information ou besoin d'aide ?",
      line2: 'Notre équipe est là pour vous accompagner.',
    };
    return {
      badgeLabel: source?.badgeLabel ?? defaults.badgeLabel,
      title: source?.title ?? defaults.title,
      line1: source?.line1 ?? defaults.line1,
      line2: source?.line2 ?? defaults.line2,
    };
  }, [previewConfig, tenant?.config, tenant?.name]);

  // Configuration SEO personnalisée par tenant
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `Contact | ${tenant?.name || 'Notre équipe'}`,
      description: seo.description || `Contactez l'équipe de ${tenant?.name || 'notre entreprise'} pour toute question, demande d'information ou besoin d'aide.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/contact`
    };
  }, [tenant]);

  // --- LOGIQUE MÉTIER CONSERVÉE À L'IDENTIQUE ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { success, error } = await sendContactEmail(formData) as SendContactEmailResponse;
      if (!success) {
        const message = typeof error === 'string' ? error : 'Erreur inconnue';
        throw new Error(message);
      }
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      toast({
        title: "Message envoyé avec succès !",
        description: "Merci de nous avoir contactés. Notre équipe vous répondra dans les meilleurs délais.",
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      toast({
        title: "Erreur d'envoi",
        description: "Le message n'a pas pu être envoyé. Veuillez réessayer ou nous contacter directement.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        image={seoConfig.image}
        url={seoConfig.url}
      />

      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {contactTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: contactTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[contactTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(contactTheme.overlay.color1, contactTheme.overlay.opacity1)},
                    ${hexToRgba(contactTheme.overlay.color2, contactTheme.overlay.opacity2)},
                    ${hexToRgba(contactTheme.overlay.color3, contactTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !contactTheme.overlay.useGradient
                ? hexToRgba(contactTheme.overlay.color1, contactTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {contactTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(contactTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[contactTheme.icons[0].icon] || iconMap.Wrench;
                return IconComponent ? <IconComponent size={contactTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {contactTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(contactTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[contactTheme.icons[1].icon] || iconMap.Server;
                return IconComponent ? <IconComponent size={contactTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {contactTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(contactTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[contactTheme.icons[2].icon] || iconMap.Headset;
                return IconComponent ? <IconComponent size={contactTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {contactTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(contactTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[contactTheme.icons[3].icon] || iconMap.Shield;
                return IconComponent ? <IconComponent size={contactTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>

        {/* Contenu de la page */}
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
            {/* En-tête de la section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
              className="mb-8 py-16 text-center"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 mb-8 shadow-xl"
                style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d` }}
              >
                <Headset className="w-5 h-5 mr-3 animate-pulse" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                <span className="font-semibold text-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{contactHeader.badgeLabel}</span>
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
                className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg"
                style={{ color: tenant?.config?.color3 || '#ffffff' }}
              >
                {contactHeader.title}
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
                className="text-lg drop-shadow-md max-w-3xl mx-auto"
                style={{ color: `${tenant?.config?.color3 || '#ffffff'}e6` }}
              >
                {contactHeader.line1}
                <br />
                <span className="font-semibold" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>{contactHeader.line2}</span>
              </motion.p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
              {/* Colonne de gauche : Informations (Logique INCHANGÉE, style adapté) */}
              <motion.div
                initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white/5 border border-white/10 text-white backdrop-blur-sm shadow-xl rounded-2xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                  <Phone className="mr-3" size={24} style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                  Nos coordonnées
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="p-3 rounded-lg flex-shrink-0" 
                      style={{ backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}33` }}
                    >
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-1">Téléphone</h3>
                      <p className="text-white/70">
                        {[tenant?.config?.phone, tenant?.config?.phone2, tenant?.config?.phone3, tenant?.config?.phone4]
                          .filter(phone => phone && phone.trim() !== '')
                          .join(' / ') || 'Non spécifié'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div 
                      className="p-3 rounded-lg flex-shrink-0" 
                      style={{ backgroundColor: `${tenant?.config?.color2 || '#008aef'}33` }}
                    >
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-1">Email</h3>
                      <p className="text-white/70">{[tenant?.email, tenant?.config?.email2]
                          .filter(email => email && email.trim() !== '')
                          .join(' / ') || 'Non spécifié'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div 
                      className="p-3 rounded-lg flex-shrink-0" 
                      style={{ backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}33` }}
                    >
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-1">Adresse</h3>
                      <p className="text-white/70">{tenant?.config?.address}</p>
                    </div>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.4 }}
                  className="bg-white/95 border border-white/20 backdrop-blur-sm shadow-xl rounded-lg overflow-hidden h-96 mt-8"
                >
                  <iframe
                    src={tenant?.config?.google_maps}
                    width="100%" height="100%" style={{ border: 0 }}
                    allowFullScreen loading="lazy" title="Carte de localisation"
                  ></iframe>
                </motion.div>
              </motion.div>

              {/* Colonne de droite : Formulaire (Style adapté à SIS) */}
              <motion.div
                initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white/5 border border-white/10 text-white backdrop-blur-sm shadow-xl rounded-2xl p-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <Mail className="mr-3" size={24} style={{ color: tenant?.config?.color1 || '#fe8d17' }}/>
                  Formulaire de contact
                </h2>
                <p className="text-white/70 mb-6 leading-relaxed">Remplissez ce formulaire pour nous faire part de votre demande. Nous nous engageons à vous répondre rapidement.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="text" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#fe8d17] focus:border-[#fe8d17] outline-none transition-all duration-300" placeholder="Votre nom complet" required />
                  <input type="email" name="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#fe8d17] focus:border-[#fe8d17] outline-none transition-all duration-300" placeholder="Votre adresse email" required />
                  <input type="tel" name="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#fe8d17] focus:border-[#fe8d17] outline-none transition-all duration-300" placeholder="Votre numéro de téléphone (Optionnel)" />
                  
                  <Select
                    value={formData.subject}
                    onValueChange={(value) => setFormData({ ...formData, subject: value })}
                    hoverColor={hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.95)}
                    selectedColor={hexToRgba(tenant?.config?.color2 || '#008aef', 0.5)}
                  >
                    <SelectTrigger
                      className="w-full px-4 py-3 rounded-lg transition-all duration-300"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        color: tenant?.config?.color3 || '#ffffff'
                      }}
                      onFocusStyle={{
                        borderColor: tenant?.config?.color1 || '#fe8d17',
                        backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1)
                      }}
                    >
                      <SelectValue placeholder="Objet de votre demande..." />
                    </SelectTrigger>
                    <SelectContent
                      style={{
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        backgroundColor: hexToRgba(tenant?.config?.color2 || '#fe8d17', 0.95),
                        backdropFilter: 'blur(16px)'
                      }}
                    >
                      {subjectOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <textarea name="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={5} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-[#fe8d17] focus:border-[#fe8d17] outline-none transition-all duration-300" placeholder="Votre message..." required />
                  
                  <button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="w-full flex items-center justify-center px-6 py-4 font-bold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ 
                      backgroundColor: tenant?.config?.color1 || '#fe8d17',
                      color: tenant?.config?.color3 || '#ffffff'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}dd`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
                    }}
                  >
                    {isSubmitting ? (
                      <><Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />Envoi en cours...</>
                    ) : (
                      <><Mail className="mr-2 h-5 w-5" />Envoyer le message</>
                    )}
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};